import React, { useState, useEffect } from 'react';
import { getUserCallHistory, getCallStats } from '../services/api';

function CallHistory() {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    dateFrom: '',
    dateTo: ''
  });
  const [itemsPerPage, setItemsPerPage] = useState(50);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const [historyResponse, statsResponse] = await Promise.all([
        getUserCallHistory(page, itemsPerPage),
        getCallStats()
      ]);

      if (historyResponse.success) {
        let filteredData = historyResponse.data || [];
        
        // Apply filters
        if (filters.status) {
          filteredData = filteredData.filter(call => call.status === filters.status);
        }
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filteredData = filteredData.filter(call => 
            call.to_number?.toLowerCase().includes(searchLower) ||
            call.call_context?.expected_person_name?.toLowerCase().includes(searchLower) ||
            call.call_context?.company_name?.toLowerCase().includes(searchLower)
          );
        }
        if (filters.dateFrom) {
          filteredData = filteredData.filter(call => new Date(call.created_at) >= new Date(filters.dateFrom));
        }
        if (filters.dateTo) {
          filteredData = filteredData.filter(call => new Date(call.created_at) <= new Date(filters.dateTo));
        }
        
        setHistory(filteredData);
        setTotalPages(historyResponse.pagination?.totalPages || 1);
      }

      if (statsResponse.success) {
        setStats(statsResponse.stats);
      }
    } catch (err) {
      console.error('Error loading call history:', err);
      setError(err.response?.data?.message || 'Failed to load call history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [page, itemsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadHistory();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const clearFilters = () => {
    setFilters({ status: '', search: '', dateFrom: '', dateTo: '' });
  };

  if (loading && history.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0e27] flex items-center justify-center relative z-10">
        <div className="text-center">
          <div className="animate-spin border-4 border-blue-500/30 border-t-blue-500 h-12 w-12 mx-auto rounded-full"></div>
          <p className="mt-4 text-gray-400">Loading call history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white py-8 px-4 relative z-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-white mb-2">Call History</h1>
          <p className="text-sm text-gray-400">View and manage your call records</p>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-4 backdrop-blur-sm">
              <div className="text-xs text-gray-400 mb-1">Total Calls</div>
              <div className="text-2xl font-semibold text-white">{stats.total_calls || 0}</div>
            </div>
            <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-4 backdrop-blur-sm">
              <div className="text-xs text-gray-400 mb-1">Successful</div>
              <div className="text-2xl font-semibold text-green-400">{stats.successful_calls || 0}</div>
            </div>
            <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-4 backdrop-blur-sm">
              <div className="text-xs text-gray-400 mb-1">Failed</div>
              <div className="text-2xl font-semibold text-red-400">{stats.failed_calls || 0}</div>
            </div>
            <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-4 backdrop-blur-sm">
              <div className="text-xs text-gray-400 mb-1">Pending</div>
              <div className="text-2xl font-semibold text-yellow-400">{stats.pending_calls || 0}</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-4 mb-6 backdrop-blur-sm">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <input
              type="text"
              placeholder="Search phone, name, company..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="px-3 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
            />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="px-3 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
            />
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="px-3 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
            />
            <div className="flex gap-2">
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-700 transition-all"
              >
                Clear
              </button>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="20">20/page</option>
                <option value="50">50/page</option>
                <option value="100">100/page</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg backdrop-blur-sm">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Call History Table */}
        {history.length === 0 ? (
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-12 text-center backdrop-blur-sm">
            <div className="text-4xl mb-4">📭</div>
            <p className="text-gray-400">No call history found</p>
          </div>
        ) : (
          <div className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg overflow-hidden backdrop-blur-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#0a0e27] border-b border-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Person</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {history.map((call) => (
                    <tr key={call.id} className="hover:bg-[#0a0e27]/50 transition-colors">
                      <td className="px-4 py-3 text-sm text-white">{call.to_number}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          call.status === 'completed' || call.status === 'success' 
                            ? 'bg-green-900/30 text-green-400 border border-green-500/30' 
                            : call.status === 'failed' 
                            ? 'bg-red-900/30 text-red-400 border border-red-500/30'
                            : 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30'
                        }`}>
                          {call.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        {call.call_context?.expected_person_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">{formatDate(call.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {call.call_duration ? `${call.call_duration}s` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setExpandedId(expandedId === call.id ? null : call.id)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-all"
                        >
                          {expandedId === call.id ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Expanded Details */}
            {expandedId && history.find(c => c.id === expandedId) && (() => {
              const call = history.find(c => c.id === expandedId);
              return (
                <div className="border-t border-gray-700 bg-[#0a0e27]/50 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-3">Call Context</h4>
                      <div className="bg-black/50 rounded-lg p-4 text-sm space-y-2 border border-gray-700">
                        <div><span className="text-gray-400">Person:</span> <span className="text-white">{call.call_context?.expected_person_name || 'N/A'}</span></div>
                        <div><span className="text-gray-400">Agent:</span> <span className="text-white">{call.call_context?.agent_name || 'N/A'}</span></div>
                        <div><span className="text-gray-400">Company:</span> <span className="text-white">{call.call_context?.company_name || 'N/A'}</span></div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-300 mb-3">Call Details</h4>
                      <div className="bg-black/50 rounded-lg p-4 text-sm space-y-2 border border-gray-700">
                        {call.call_id && <div><span className="text-gray-400">Call ID:</span> <span className="text-white">{call.call_id}</span></div>}
                        {call.call_sid && <div><span className="text-gray-400">Call SID:</span> <span className="text-white">{call.call_sid}</span></div>}
                        {call.call_duration && <div><span className="text-gray-400">Duration:</span> <span className="text-white">{call.call_duration}s</span></div>}
                      </div>
                    </div>
                  </div>

                  {/* Recording */}
                  {call.recording_url && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-300 mb-3">Recording</h4>
                      <div className="bg-black/50 rounded-lg p-4 border border-gray-700">
                        <audio controls className="w-full">
                          <source src={call.recording_url} type="audio/wav" />
                          <source src={call.recording_url} type="audio/mpeg" />
                        </audio>
                        <a
                          href={call.recording_url}
                          download
                          className="mt-3 inline-block px-4 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-all"
                        >
                          Download
                        </a>
                      </div>
                    </div>
                  )}

                  {/* AI Analysis */}
                  {(call.correct_person !== null || call.interested_in_marketwavegen !== null) && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-300 mb-3">AI Analysis</h4>
                      <div className="bg-black/50 rounded-lg p-4 space-y-3 border border-gray-700">
                        {call.correct_person !== null && (
                          <div>
                            <span className="text-gray-400 text-sm">Correct Person:</span>
                            <span className={`ml-2 px-2 py-1 text-xs rounded ${
                              call.correct_person ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                            }`}>
                              {call.correct_person ? 'Yes' : 'No'}
                            </span>
                          </div>
                        )}
                        {call.interested_in_marketwavegen !== null && (
                          <div>
                            <span className="text-gray-400 text-sm">Interested in ConvrsAI by Market Wavegen:</span>
                            <span className={`ml-2 px-2 py-1 text-xs rounded ${
                              call.interested_in_marketwavegen ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                            }`}>
                              {call.interested_in_marketwavegen ? 'Yes' : 'No'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between bg-[#0a0e27]/50">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CallHistory;
